import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ListBox, Select } from '@heroui/react';
import type { OperationPairRole } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { PAIR_ROLES, pairRoleChip } from '../../operationEditorMeta';

type PairRoleChipProps = {
    pairGroup: number;
    role: OperationPairRole;
    isReadOnly: boolean;
    onChange: (role: OperationPairRole) => void;
};

const CHIP =
    'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest';

/**
 * What this pair does — one chip, from a closed list.
 *
 * The chip **is** the trigger: the `Select.Trigger` is styled as the chip
 * itself, with no separate dropdown beside it. A chevron and a control well
 * would double the width of a header row whose whole job is to be scannable
 * down a column of pairs, and the tint already reads as a value rather than as
 * a heading.
 *
 * It is never empty: a pair with no considered role reads `Other`, which is a
 * real answer rather than a prompt. That is what keeps the taxonomy closed —
 * nobody needs to invent an eighth role to describe a pair that does not fit.
 *
 * Read-only renders the same chip as a plain span, not a disabled button: a
 * BranchAdmin is reading a label, and a greyed-out control tells them something
 * is broken rather than that it is not theirs to change.
 */
export const PairRoleChip: React.FC<PairRoleChipProps> = ({
    pairGroup,
    role,
    isReadOnly,
    onChange,
}) => {
    const { t } = useTranslation('pages');
    const current = pairRoleChip(role);

    if (isReadOnly) {
        return (
            <span
                className={cn(CHIP, current.chip)}
                {...getNodeTestIdProp(
                    `operation-editor-pair-role-${pairGroup}`,
                )}
            >
                {t(current.labelKey)}
            </span>
        );
    }

    return (
        <Select
            aria-label={t('operations.editor.pairRoleAria', {
                number: pairGroup,
            })}
            className="w-fit"
            onChange={(next) => next && onChange(next as OperationPairRole)}
            value={role}
        >
            <Select.Trigger
                className={cn(
                    CHIP,
                    current.chip,
                    'h-auto min-h-0 border-0 shadow-none hover:opacity-80',
                )}
                {...getNodeTestIdProp(
                    `operation-editor-pair-role-${pairGroup}`,
                )}
            >
                <Select.Value />
            </Select.Trigger>
            <Select.Popover>
                <ListBox>
                    {PAIR_ROLES.map((entry) => (
                        <ListBox.Item
                            id={entry.id}
                            key={entry.id}
                            textValue={t(entry.labelKey)}
                        >
                            <span className={cn(CHIP, entry.chip)}>
                                {t(entry.labelKey)}
                            </span>
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    ))}
                </ListBox>
            </Select.Popover>
        </Select>
    );
};
